/**
 * Commissions — tiered achievement model (TL / BD employees).
 *
 * Per BD: Actual (collected revenue, FX-converted to their target currency) ÷
 * Target → achievement %. That % lands in a rate TIER (e.g. 100–135% → 11%).
 * Commission earned = collected × tier rate. Open (planned, not-yet-collected)
 * payments give the "theoretic if all collected"; the delta is the collection gap.
 * Commission paid is tracked per quarter to show what's still owed.
 *
 * P1 is config only (tiers / targets / FX). Source ingest + the calc engine +
 * the report come in later phases. All config is type-only on AppConfig (partial
 * updateDoc, no schema/migration), edited at /admin/config/commissions.
 */
export const COMMISSION_CURRENCIES = ['EUR', 'USD', 'CHF', 'GBP', 'CLP', 'MXN', 'AUD'];
export const COMMISSION_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
export const DEFAULT_COMMISSION_TIERS = [
    { label: '0–50%', upToPct: 0.5, rate: 0.02 },
    { label: '50–80%', upToPct: 0.8, rate: 0.04 },
    { label: '80–100%', upToPct: 1.0, rate: 0.06 },
    { label: '100–135%', upToPct: 1.35, rate: 0.11 },
    { label: '135%+', upToPct: null, rate: 0.14 },
];
export function resolveCommissionYear(config, year) {
    return config?.commissionYears?.find((y) => y.year === year);
}
export function resolveCommissionTiers(config, year) {
    const t = (year != null ? resolveCommissionYear(config, year)?.tiers : undefined) ?? config?.commissionTiers;
    return t && t.length ? t : DEFAULT_COMMISSION_TIERS;
}
/** The commission rate for a given achievement fraction (Actual/Target). */
export function commissionTierRate(tiers, achievementPct) {
    for (const t of tiers) {
        if (t.upToPct === null)
            return t.rate;
        if (achievementPct < t.upToPct)
            return t.rate;
    }
    return tiers[tiers.length - 1]?.rate ?? 0;
}
/** The tiers that apply to one target: its own custom tiers if set, else the standard ones. */
export function commissionTiersForTarget(target, standard) {
    const c = target?.customTiers;
    return c && c.length ? c : standard;
}
export function resolveCommissionTargets(config, year) {
    return (year != null ? resolveCommissionYear(config, year)?.targets : undefined) ?? config?.commissionTargets ?? [];
}
export function findCommissionTarget(config, bdKey, year) {
    return resolveCommissionTargets(config, year).find((t) => t.bdKey === bdKey) ?? null;
}
export function resolveCommissionFx(config) {
    return config?.commissionFx ?? [];
}
/** Merge an override FX table onto a base one — an override rate (>0) wins per
 *  year/currency/month; blank/zero cells fall back to the base. Used by the
 *  agents layer (custom historic rates over the general auto-sourced table). */
export function mergeCommissionFx(base, override) {
    if (!override?.length)
        return base;
    const years = new Set([...base, ...override].map((f) => f.year));
    return [...years].map((year) => {
        const b = base.find((f) => f.year === year)?.rates ?? {};
        const o = override.find((f) => f.year === year)?.rates ?? {};
        const currencies = new Set([...Object.keys(b), ...Object.keys(o)]);
        const rates = {};
        for (const cur of currencies) {
            const ba = b[cur] ?? [];
            const oa = o[cur] ?? [];
            rates[cur] = Array.from({ length: 12 }, (_, i) => typeof oa[i] === 'number' && oa[i] > 0 ? oa[i] : (ba[i] ?? 0));
        }
        return { year, rates };
    });
}
/** Monthly FX rate for a currency (1-based month), or null if not set. */
export function commissionFxRate(fx, year, currency, month1) {
    const y = fx.find((f) => f.year === year);
    const arr = y?.rates[currency];
    const v = arr?.[month1 - 1];
    return typeof v === 'number' && v > 0 ? v : null;
}
/** The →CHF rate for a month, or — when that month has none (CLP before the monthly sourcing started, a
 *  year the table does not cover) — the NEAREST month that has one for that currency: same year first,
 *  then the closest year. `exact` says which. Null only when the table has no rate for the currency at
 *  all. Used by USL so a missing month never falls back to 1:1 (27 Oct 2026: CLP 2024 counted as CHF). */
export function commissionFxRateNearest(fx, year, currency, month1) {
    if (currency === 'CHF')
        return { rate: 1, exact: true };
    const exact = commissionFxRate(fx, year, currency, month1);
    if (exact != null)
        return { rate: exact, exact: true };
    let best = null;
    for (const y of fx) {
        const arr = y.rates[currency] ?? [];
        for (let i = 0; i < 12; i++) {
            const v = arr[i];
            if (typeof v !== 'number' || !(v > 0))
                continue;
            const dist = Math.abs((y.year - year) * 12 + (i + 1 - month1));
            if (!best || dist < best.dist)
                best = { rate: v, dist };
        }
    }
    return best ? { rate: best.rate, exact: false } : null;
}
// ─────────────────────────────────────────────────────────────────────────────
// Source ingest (P2) — quarterly Raw SFDC + Memberships uploads
// ─────────────────────────────────────────────────────────────────────────────
export const COMMISSION_SOURCE_KINDS = ['sfdc', 'memberships'];
export const COMMISSION_SOURCE_LABELS = {
    sfdc: 'Raw SFDC (payments)',
    memberships: 'Memberships',
};
// ─────────────────────────────────────────────────────────────────────────────
// Assessment cycles — a fiscal year has 6: Q1–Q4 of the FY, then Q1 & (final)
// Q2 of the FOLLOWING calendar year (collections keep landing after FY end).
// ─────────────────────────────────────────────────────────────────────────────
export const COMMISSION_CYCLES = [1, 2, 3, 4, 5, 6];
export const COMMISSION_CYCLE_COUNT = 6;
/** The calendar (year, quarter) cutoff an assessment cycle collects through. */
export function commissionCycleCutoff(fiscalYear, cycle) {
    return cycle <= 4 ? { year: fiscalYear, quarter: cycle } : { year: fiscalYear + 1, quarter: cycle - 4 };
}
/** Last calendar day (ISO) of the cycle's cutoff quarter, e.g. 2026-C4 → "2026-12-31". */
export function commissionCycleEndDate(fiscalYear, cycle) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    const lastMonth = quarter * 3;
    const day = new Date(Date.UTC(year, lastMonth, 0)).getUTCDate();
    return `${year}-${String(lastMonth).padStart(2, '0')}-${day}`;
}
/** True once the cycle's quarter is over (`todayISO` after its last day). Payouts may
 *  only be confirmed and statements released then (Oct 2026: Q4 was released on 5 Oct). */
export function commissionCycleEnded(fiscalYear, cycle, todayISO) {
    return todayISO > commissionCycleEndDate(fiscalYear, cycle);
}
/** Last day (ISO) of an agent month "YYYY-MM". */
export function commissionMonthEndDate(monthId) {
    const [y, m] = monthId.split('-').map(Number);
    const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return `${monthId}-${String(day).padStart(2, '0')}`;
}
/** Today as ISO YYYY-MM-DD in Europe/Zurich (the business calendar). */
export function commissionTodayZurich(now = new Date()) {
    return now.toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
}
/** The current live assessment from today's date: FY = calendar year, cycle = calendar quarter (1–4). */
export function commissionCurrentAssessment(now) {
    return { fiscalYear: now.getUTCFullYear(), cycle: Math.floor(now.getUTCMonth() / 3) + 1 };
}
/** e.g. "Q1 2026" … "Q4 2026", "Q1 2027", "Q2 2027 (final)". */
export function commissionCycleLabel(fiscalYear, cycle) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    return `Q${quarter} ${year}${cycle === 6 ? ' (final)' : ''}`;
}
export function commissionPeriodId(fiscalYear, cycle) {
    return `${fiscalYear}-C${cycle}`;
}
/**
 * The FX month used to convert the payable into the payout currency = the LAST
 * month of the cycle's quarter (e.g. Q2 → June; cycle 5 = Q1 next FY → March).
 */
export function commissionPayoutFxMonth(fiscalYear, cycle) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    return { year, month1: quarter * 3 };
}
/**
 * Statement filename: "Variable Compensation_{name}_Q{q}_FY{yy}", where Q/FY are
 * the assessment's CALENDAR quarter + year (so FY26's 5th cycle, collected
 * through Q1 2027, is "…_Q1_FY27").
 */
export function commissionStatementFilename(displayName, fiscalYear, cycle) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    const name = displayName.replace(/[\\/:*?"<>|]+/g, ' ').trim();
    return `Variable Compensation_${name}_Q${quarter}_FY${String(year).slice(2)}`;
}
/** e.g. "Q2 FY26" — the assessment's calendar quarter + fiscal-year short form. */
export function commissionQuarterFyLabel(fiscalYear, cycle) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    return `Q${quarter} FY${String(year).slice(2)}`;
}
/**
 * Map a user's `employmentEntity` to a Monthly-Mutations area (legal entity).
 * Exact match first, then a prefix match either way (so "DAAily platforms GmbH"
 * resolves to the "DAAily platforms GmbH – paymi" area), else the first area.
 */
export function mutationAreaForEntity(entity, areas) {
    if (!areas.length)
        return '';
    if (!entity)
        return areas[0];
    const exact = areas.find((a) => a === entity);
    if (exact)
        return exact;
    const pref = areas.find((a) => a.startsWith(entity) || entity.startsWith(a));
    return pref ?? areas[0];
}
/** Absolute quarter ordinal (year*4 + quarter-1) — for cumulative window math. */
export function commissionQuarterOrdinal(year, quarter) {
    return year * 4 + (quarter - 1);
}
// ─────────────────────────────────────────────────────────────────────────────
// FX conversion — cross-rate through the base (CHF)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Convert an amount from one currency to a target currency using the monthly
 * average FX (stored as "1 unit currency = N CHF"). Cross-rate through CHF:
 *   amountTarget = amount × rate[from] / rate[target].
 * CHF has rate 1. Returns null if a needed rate is missing.
 */
export function commissionConvertToTarget(fx, amount, fromCurrency, targetCurrency, year, month1) {
    const from = fromCurrency === 'CHF' ? 1 : commissionFxRate(fx, year, fromCurrency, month1);
    const target = targetCurrency === 'CHF' ? 1 : commissionFxRate(fx, year, targetCurrency, month1);
    if (from == null || target == null || target === 0)
        return null;
    return (amount * from) / target;
}
export const DEFAULT_COMMISSION_MEMBERSHIP_BONUS = [
    { minCount: 8, rate: 0.015 },
    { minCount: 11, rate: 0.02 },
    { minCount: 13, rate: 0.025 },
    { minCount: 15, rate: 0.06 },
];
export function resolveCommissionMembershipBonus(config, year) {
    const t = (year != null ? resolveCommissionYear(config, year)?.membershipBonus : undefined) ??
        config?.commissionMembershipBonus;
    return t && t.length ? t : DEFAULT_COMMISSION_MEMBERSHIP_BONUS;
}
/** The bonus rate for a membership count (highest qualifying tier), 0 if none. */
export function commissionMembershipBonusRate(tiers, count) {
    let rate = 0;
    for (const t of [...tiers].sort((a, b) => a.minCount - b.minCount)) {
        if (count >= t.minCount)
            rate = t.rate;
    }
    return rate;
}
/** Default Team-Lead accrual rate (2% of collected team sales). */
export const COMMISSION_TL_ACCRUAL_RATE = 0.02;
export function resolveCommissionAmountBasis(config) {
    return config?.commissionAmountBasis === 'salesforceTotal' ? 'salesforceTotal' : 'products';
}
export function commissionPaidId(fiscalYear, bdKey) {
    return `${fiscalYear}__${bdKey}`;
}
export const ZERO_CYCLES = [0, 0, 0, 0, 0, 0];
/** Normalize a paid array to length 6 (missing → 0). */
export function normalizeCycleAmounts(arr) {
    return Array.from({ length: COMMISSION_CYCLE_COUNT }, (_, i) => arr?.[i] || 0);
}
export function sumCycles(arr) {
    return normalizeCycleAmounts(arr).reduce((s, n) => s + n, 0);
}
/**
 * Sum of payouts in cycles STRICTLY BEFORE `cycle` (1-based). A payout confirmed
 * FOR an assessment is only counted as "paid" from the NEXT assessment on — so a
 * Q2 payout never appears as paid in the Q2 statement, only from Q3.
 */
export function sumCyclesBefore(arr, cycle) {
    return normalizeCycleAmounts(arr)
        .slice(0, Math.max(0, cycle - 1))
        .reduce((s, n) => s + n, 0);
}
export function buildCommissionStatementRow(row, paid, cycle) {
    const commissionPaidByCycle = normalizeCycleAmounts(paid?.commissionPaidByCycle);
    const membershipPaidByCycle = normalizeCycleAmounts(paid?.membershipPaidByCycle);
    // "Already paid" = payouts from PRIOR assessments only (current-cycle payout is
    // paid next quarter, so it never reduces this quarter's payable).
    const paidTotal = sumCyclesBefore(commissionPaidByCycle, cycle);
    const payable = row.earned - paidTotal;
    const membershipPaidTotal = sumCyclesBefore(membershipPaidByCycle, cycle);
    const membershipPayable = row.membershipBonusEarned - membershipPaidTotal;
    const fx = row.payoutFxRate || 1;
    return {
        ...row,
        commissionPaidByCycle,
        membershipPaidByCycle,
        paidTotal,
        payable,
        membershipPaidTotal,
        membershipPayable,
        deltaMissingCollection: row.theoreticIfAllCollected - (paidTotal + payable),
        payableInPayout: payable * fx,
        membershipPayableInPayout: membershipPayable * fx,
    };
}
/** The accrual rate for a BD: at-target (100%) tier, or the realized tier once collected passes 100%. */
export function commissionAccrualRate(tiers, achievementPct) {
    return commissionTierRate(tiers, Math.max(1, achievementPct));
}
function round2(n) {
    return Math.round(n * 100) / 100;
}
/** Month name for a 1-based calendar month, e.g. 3 → "March". */
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
/** The +1 month (name + 2-digit year) after a 1-based month/year. */
function monthPlusOne(year, month1) {
    let m = month1 + 1;
    let y = year;
    if (m > 12) {
        m = 1;
        y += 1;
    }
    return { name: MONTH_NAMES[m - 1], yy: String(y).slice(2) };
}
/**
 * Append one cycle's four columns to the accrual doc (in place on a copy) using
 * each BD's report figures. Returns a NEW doc. Rows without an input are left
 * as-is (padded with their last balance) so the table stays rectangular.
 */
export function extendCommissionAccrual(doc, fiscalYear, cycle, tiers, inputs) {
    const { year, quarter } = commissionCycleCutoff(fiscalYear, cycle);
    const qMonth = MONTH_NAMES[quarter * 3 - 1]; // last month of the quarter
    const yy = String(year).slice(2);
    const nxt = monthPlusOne(year, quarter * 3);
    const newCols = [
        `Accrual ${qMonth} ${yy}`,
        `Accrual End ${qMonth} ${yy}`,
        `Payable ${nxt.name} ${nxt.yy}`,
        `Accrual End ${nxt.name} ${nxt.yy}`,
    ];
    const byKey = new Map(inputs.map((i) => [i.bdKey, i]));
    const rows = doc.rows.map((row) => {
        const balanceBefore = row.values[row.values.length - 1] ?? 0;
        const input = byKey.get(row.bdKey);
        if (!input) {
            // No data this cycle — carry the balance flat.
            return { ...row, values: [...row.values, 0, balanceBefore, 0, balanceBefore] };
        }
        const increment = input.cumulativeSold - row.baselineCumulativeSold;
        const rate = commissionAccrualRate(input.tiers ?? tiers, input.achievementPct);
        const booked = round2(increment * rate);
        const endQuarter = round2(balanceBefore + booked);
        const payout = round2(input.payout);
        const endAfter = round2(endQuarter - payout);
        return {
            ...row,
            values: [...row.values, booked, endQuarter, payout, endAfter],
            baselineCumulativeSold: input.cumulativeSold,
            baselineThroughCycle: cycle,
        };
    });
    return { ...doc, columns: [...doc.columns, ...newCols], rows };
}
//# sourceMappingURL=commissions.js.map